/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Grid from "@mui/material/Grid";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, MenuItem } from "@mui/material";

import { RootState } from "../../../../../store";
import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { useRooms } from "../../../../../hooks/query/useRooms";
import { notificationAdd } from "../../../../../actions/notification";
import { usePostBed, usePatchBed } from "../../../../../hooks/query/useBeds";
import { Bed, BedFormValue } from "../../../../../interfaces/BedInterface";

interface BedCreateEditProps {
  onClose: () => void;
  bed?: Partial<Bed> | null;
  onSuccess: (data?: Bed | Bed[]) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function BedCreateEdit({ onClose, bed, onSuccess }: BedCreateEditProps): React.ReactNode {
  const isEdit = !!bed?.id;

  // hooks
  const dispatch = useDispatch();
  const { data: rooms = [] } = useRooms();
  const { mutate: postBedMutation } = usePostBed();
  const { mutate: patchBedMutation } = usePatchBed();

  // selectors
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentre?.id);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<BedFormValue>({
    defaultValues: {
      roomId: bed?.roomId || null,
      numberOfBeds: 1,
      customName: bed?.customName || ""
    }
  });

  const onSubmit = async (data: BedFormValue) => {
    if (!rcId) return;

    if (!data.roomId) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Please select a room.",
          autoTimeout: true
        })
      );
      return;
    }

    if (!isEdit && (!data.numberOfBeds || data.numberOfBeds <= 0)) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Please enter a valid number of beds.",
          autoTimeout: true
        })
      );
      return;
    }

    if (isEdit && !data.customName) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Custom name is required when editing.",
          autoTimeout: true
        })
      );
      return;
    }

    const payload: BedFormValue = {
      ...data,
      resourceCentreId: rcId
    };

    if (isEdit && bed?.id) {
      patchBedMutation(
        { id: bed.id, data: payload },
        {
          onSuccess: (response: any) => {
            onSuccess(response);
            onClose();
          }
        }
      );
    } else {
      postBedMutation(payload, {
        onSuccess: (response: any) => {
          onSuccess(response);
          onClose();
        }
      });
    }
  };

  return (
    <Panel
      title={isEdit ? "Edit Bed" : "Create Bed(s)"}
      onClose={onClose}
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button onClick={onClose}>{tl("cancel")}</Button>
          <Button
            type="submit"
            variant="contained"
            form="bed-form"
            disabled={isSubmitting || rooms.length === 0}
          >
            {isEdit ? tl("update") : tl("create")}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <form id="bed-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            {/* Room Select */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="roomId"
                control={control}
                rules={{ required: "Room is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    label="Select Room *"
                    value={field.value || ""}
                    error={!!errors.roomId}
                    helperText={errors.roomId?.message || ""}
                    disabled={isEdit}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  >
                    {rooms.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name} ({room.code})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* If Edit, show Bed Number & Custom Name */}
            {isEdit ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="customName"
                    control={control}
                    rules={{ required: "Custom name is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        label="Custom Name"
                        error={!!errors.customName}
                        helperText={errors.customName?.message}
                        onFocus={(event) => event.target.select()}
                        slotProps={{
                          inputLabel: { shrink: true }
                        }}
                        placeholder="e.g. ROOM-100"
                        value={field.value || ""}
                      />
                    )}
                  />
                </Grid>
              </>
            ) : (
              /* If Create, show Number of Beds to create */
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="numberOfBeds"
                  control={control}
                  rules={{ required: "Number of beds is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      variant="outlined"
                      label="Number of Beds *"
                      error={!!errors.numberOfBeds}
                      helperText={errors.numberOfBeds?.message}
                      onFocus={(event) => event.target.select()}
                      slotProps={{
                        inputLabel: { shrink: true }
                      }}
                      placeholder="e.g. 5"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
